import { useCallback } from 'react'
import styled, { css } from 'styled-components'

import { Icon, Layout, Pagination, Table, Text, TextField } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'
import { type PaginationChangeEventDetails } from '@loadsmart/miranda-wc'

import { LoadingState } from 'core/ui/async-content/LoadingState'
import { formatDate } from 'core/utils/date'

import { ErrorState } from 'user/ui/ErrorState'

import { type Shipper } from '../domain/shippers'
import { formatLastShipmentLabel } from '../presenters/shipperPresenter'

const TableRow = styled(Table.Row)<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${toCSSValue('color-background-highlight')};
      }
    `}
`

export type ShipperListTableProps = {
  shippers: Shipper[]
  isLoading: boolean
  isError: boolean
  totalCount: number
  currentPage: number
  pageSize: number
  searchQuery: string
  onPageChange: (page: number) => void
  onSearchChange: (query: string) => void
  onRowClick?: (shipper: Shipper) => void
}

export function ShipperListTable({
  shippers,
  isLoading,
  isError,
  totalCount,
  currentPage,
  pageSize,
  searchQuery,
  onPageChange,
  onSearchChange,
  onRowClick,
}: ShipperListTableProps) {
  const handlePaginationChange = useCallback(
    ({ detail }: CustomEvent<PaginationChangeEventDetails>) => {
      onPageChange(detail.page)
    },
    [onPageChange]
  )

  return (
    <Table>
      <Table.Toolbar>
        <Layout.Group justify="space-between" align="center">
          <TextField
            type="search"
            data-testid="shipper-search"
            placeholder="Search"
            value={searchQuery}
            leading={<Icon name="search" size="16px" color="color-text-tertiary" />}
            onInput={event => {
              onSearchChange((event.target as HTMLInputElement).value)
            }}
            onClear={() => onSearchChange('')}
            style={{ maxWidth: '320px' }}
            disabled={totalCount === 0 && searchQuery === ''}
          />
          <Text variant="body-md" color="color-text-tertiary">
            {totalCount} results
          </Text>
        </Layout.Group>
      </Table.Toolbar>
      <Table.Head>
        <Table.Row>
          <Table.Cell>Shipper</Table.Cell>
          <Table.Cell>Date added</Table.Cell>
          <Table.Cell>Last shipment</Table.Cell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {!isLoading &&
          shippers.map(shipper => {
            const lastShipmentLabel = formatLastShipmentLabel(shipper)

            return (
              <TableRow
                key={shipper.id}
                $clickable={!!onRowClick}
                onClick={() => onRowClick?.(shipper)}
              >
                <Table.Cell>
                  <Text variant="body-md">{shipper.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text variant="body-md">
                    {formatDate(shipper.carrierAddedAt, 'MMM dd, yyyy')}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text variant="body-md">{lastShipmentLabel}</Text>
                </Table.Cell>
              </TableRow>
            )
          })}
      </Table.Body>
      {isLoading && (
        <Table.Feedback>
          <Layout.Box>
            <LoadingState />
          </Layout.Box>
        </Table.Feedback>
      )}
      {isError && (
        <Table.Feedback>
          <Layout.Box>
            <ErrorState />
          </Layout.Box>
        </Table.Feedback>
      )}
      <Table.Actions sticky>
        <Pagination
          data-testid="shipper-list-pagination"
          count={totalCount}
          page={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
        />
      </Table.Actions>
    </Table>
  )
}
